/**
 * Colours for the dashboard's piglet registration widgets.
 *
 * Deliberately the same palette the lung/weather review and the climate views already use, so the
 * dashboard reads as one system rather than as a bolted-on screen. Indigo is the neutral "this is
 * data" colour; red is spent only on completed cycles that actually missed their target, and amber only on the
 * two weight-related cards, where the number is a target to watch rather than a failure.
 */
export const pigletRegistrationColors = {
  /** indigo-500 - a cycle that hit its expected volume. */
  registered: '#6366f1',

  /** red-500 - a completed cycle under 80% of its expected volume. The only alarming colour on the chart. */
  belowExpected: '#ef4444',

  /** gray-400 - the expected line, and axis ticks. Recessive on purpose: it is a reference, not a series. */
  expected: '#9ca3af',

  /** amber-500 - weight targets. */
  target: '#f59e0b',

  /** green-500 - a plain "here is the count" marker, no judgement attached. */
  neutral: '#22c55e',

  /**
   * Segments of a cycle bar, one per week of the cycle, bottom (cycle week 1) to top - indigo 700/500/400/300.
   * One hue stepped dark to light: the weeks are parts of one total, not competing series. A cycle longer
   * than the ramp reuses its lightest step. A 1-week cycle is drawn in `registered`, as a plain weekly bar.
   */
  cycleWeekRamp: ['#4338ca', '#6366f1', '#818cf8', '#a5b4fc'],

  /** The same ramp in red - red 700/500/400/300 - for a completed cycle below 80% of its expected volume. */
  belowExpectedRamp: ['#b91c1c', '#ef4444', '#f87171', '#fca5a5'],

  /** The cycle in progress: indigo-400 stripes on a pale indigo ground, and a dotted outline up to expected. */
  inProgress: {
    stripe: '#818cf8',
    ground: '#e8e9fb'
  },

  /** gray-100 - gridlines. */
  grid: '#f3f4f6',

  /** gray-900 - value labels above the bars. */
  ink: '#111827',

  /**
   * Birth-weight classes, in the split card and the chart's hover card. Light/medium/heavy is an ordinal
   * scale with nothing wrong at either end, so it gets one hue stepped light-to-dark rather than three
   * competing colours - indigo 400/500/700. The light end still clears 2:1 against a white card, so the
   * smallest segment of the stacked bar never dissolves into the surface.
   */
  weightClass: {
    light: '#818cf8',
    medium: '#6366f1',
    heavy: '#4338ca'
  }
};
